// client/src/pages/Game/Battlefield/utils/cardFlight.js
//
// Animations de vol de cartes — physique projectile.
//
// Principe : une courbe de Bézier quadratique B(t) = (1-t)²P0 + 2(1-t)t·P1 + t²·P2
// part de P0 avec la vitesse B'(0) = 2(P1 - P0)/T et atterrit EXACTEMENT sur P2.
// En choisissant P1 = P0 + v·T/2, on obtient le mouvement ballistique réel
// d'une carte "jetée" avec la vitesse du doigt : elle file dans la direction
// du lancer puis tombe en arc de cercle sur sa cible.

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const centerOf = (el) => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

/**
 * Anime un élément DOM le long d'une trajectoire projectile.
 *
 * @param {HTMLElement} element   l'élément à animer (positionné en fixed, centré via transform)
 * @param {Object} opts
 *   from      {x, y} px viewport — point de départ (centre de l'élément)
 *   to        {x, y} px — cible (centre visé)
 *   velocity  {x, y} px/ms — vitesse initiale (celle du pointeur au relâcher)
 *   duration  ms — optionnel (auto selon distance/vitesse, borné 240–650)
 *   rotation  {from, to} deg — inclinaison début/fin
 *   spin      deg — rotation supplémentaire en plein vol (retombe à 0 à l'arrivée)
 *   scale     {from, to}
 *   onLand    callback à l'atterrissage
 * @returns {cancel: () => void}
 */
export function animateFlight(element, {
  from,
  to,
  velocity = { x: 0, y: 0 },
  duration,
  rotation = { from: 0, to: 0 },
  spin = 0,
  scale = { from: 1, to: 1 },
  onLand,
}) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.hypot(dx, dy) || 1;
  const speed = Math.hypot(velocity.x, velocity.y);

  // Durée : proportionnelle à la distance, accélérée par la vitesse du lancer
  const T = clamp(
    duration ?? (dist / Math.max(speed, 0.85)) * 1.2,
    240,
    650
  );

  // Vitesse bornée pour éviter les trajectoires délirantes
  const vx = clamp(velocity.x, -2.2, 2.2);
  const vy = clamp(velocity.y, -2.2, 2.2);

  // Point de contrôle : vitesse initiale réelle + léger biais d'arc vers le haut
  let control;
  if (speed < 0.15) {
    // Lancer quasi immobile → arc vertical esthétique
    control = {
      x: (from.x + to.x) / 2,
      y: Math.min(from.y, to.y) - dist * 0.25,
    };
  } else {
    control = {
      x: from.x + vx * T * 0.5,
      y: from.y + vy * T * 0.5 - dist * 0.1,
    };
  }

  const start = performance.now();
  let rafId = null;
  let done = false;

  const finish = (landed) => {
    if (done) return;
    done = true;
    if (rafId) cancelAnimationFrame(rafId);
    if (landed && onLand) onLand();
  };

  const frame = (now) => {
    if (done) return;
    const t = clamp((now - start) / T, 0, 1);
    const u = 1 - t;

    // Bézier quadratique — physique projectile exacte
    const x = u * u * from.x + 2 * u * t * control.x + t * t * to.x;
    const y = u * u * from.y + 2 * u * t * control.y + t * t * to.y;

    // Rotation : interpolation + spin sinusoïdal (retombe à 0)
    const rot = rotation.from + (rotation.to - rotation.from) * t
      + spin * Math.sin(Math.PI * t);
    const s = scale.from + (scale.to - scale.from) * t;

    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
    element.style.transform = `translate(-50%, -50%) rotate(${rot}deg) scale(${s})`;

    if (t >= 1) {
      finish(true);
      return;
    }
    rafId = requestAnimationFrame(frame);
  };

  rafId = requestAnimationFrame(frame);

  return {
    cancel: () => finish(false),
  };
}

/**
 * Crée une carte volante (copie visuelle d'une carte) et la fait voler
 * du point `from` au centre de l'élément `targetEl`.
 *
 * @param {Object} opts
 *   card        données de la carte (imageUrl, attack, defense, cost…)
 *   getCardElement  fn(card) → élément ('fire', …) pour l'image de fallback
 *   from        {x, y} px viewport
 *   targetEl    HTMLElement cible (la vraie carte est masquée pendant le vol)
 *   duration    ms
 *   onLand      callback
 * @returns {cancel: () => void}
 */
export function flyCardToElement({
  card,
  getCardElement,
  from,
  targetEl,
  duration,
  onLand,
}) {
  if (!targetEl) return { cancel: () => {} };

  const to = centerOf(targetEl);
  const rect = targetEl.getBoundingClientRect();

  // R34dd : retirer l'animation CSS d'invocation AVANT le vol — ses keyframes
  // (animation-fill-mode: both) écrasent l'opacité inline pendant la lecture :
  // la vraie carte restait visible sous le clone qui vole (effet doublon).
  targetEl.classList.remove('just-summoned');

  // La vraie carte est masquée pendant le vol pour éviter le doublon
  const prevOpacity = targetEl.style.opacity;
  const prevTransition = targetEl.style.transition;
  targetEl.style.transition = 'none';
  targetEl.style.opacity = '0';

  const node = document.createElement('div');
  node.className = 'flying-card';
  node.style.cssText = `
    position: fixed;
    left: ${from.x}px;
    top: ${from.y}px;
    width: ${rect.width}px;
    height: ${rect.height}px;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 9999;
    border-radius: 5px;
    overflow: visible;
    box-shadow: 0 0 18px rgba(90, 205, 255, 0.8), 0 8px 18px rgba(0, 0, 0, 0.6);
    will-change: left, top, transform;
  `;

  const img = document.createElement('img');
  // R34kk : art réel de la carte (payload serveur sans imageUrl)
  const cfId = card && (card.cardId || card.id);
  img.src = card.imageUrl
    || (cfId ? `/api/static/images/cards/${cfId}.webp` : '')
    || `/api/static/images/cards/${getCardElement ? getCardElement(card) : 'fire'}.svg`;
  img.onerror = () => { img.src = '/api/static/images/card-backs/fire-back.svg'; };
  img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:5px;';
  node.appendChild(img);

  // Stats ATK/DEF pour les créatures
  if (card.type === 'CHARACTER' && (card.attack !== undefined || card.defense !== undefined)) {
    const stats = document.createElement('div');
    stats.style.cssText = `
      position:absolute;left:0;right:0;bottom:0;
      display:flex;justify-content:space-between;
      background-color:rgba(0,0,0,0.7);padding:3px;
      font-weight:bold;font-size:12px;color:white;border-radius:0 0 5px 5px;
    `;
    const atk = document.createElement('span');
    atk.style.cssText = 'background-color:rgba(255,0,0,0.7);padding:1px 6px;border-radius:3px;';
    atk.textContent = card.attack || 0;
    const def = document.createElement('span');
    def.style.cssText = 'background-color:rgba(0,0,102,0.7);padding:1px 6px;border-radius:3px;';
    def.textContent = card.defense || 0;
    stats.appendChild(atk);
    stats.appendChild(def);
    node.appendChild(stats);
  }

  document.body.appendChild(node);

  const flight = animateFlight(node, {
    from,
    to,
    duration: duration ?? 550,
    rotation: { from: -8, to: 0 },
    spin: 10,
    scale: { from: 1.05, to: 1 },
    onLand: () => {
      node.remove();
      // Ré-afficher la vraie carte + flash d'impact
      targetEl.style.opacity = prevOpacity || '1';
      targetEl.style.transition = prevTransition;
      flashZone(targetEl);
      if (onLand) onLand();
    },
  });

  return {
    cancel: () => {
      flight.cancel();
      node.remove();
      targetEl.style.opacity = prevOpacity || '1';
      targetEl.style.transition = prevTransition;
    },
  };
}

/**
 * Flash d'impact sur une zone (via ::after pour passer outre les !important).
 */
export function flashZone(zoneEl) {
  if (!zoneEl) return;
  zoneEl.classList.remove('zone-impact');
  // force reflow pour rejouer l'animation si déjà présente
  void zoneEl.offsetWidth;
  zoneEl.classList.add('zone-impact');
  setTimeout(() => zoneEl.classList.remove('zone-impact'), 550);
}
